import { ApplicationShellController } from "./controllers/application-shell-controller";
import { CoverageSlidersDisclosure } from "./controllers/coverage-sliders-disclosure";
import { BillingFocusController } from "./controllers/billing-focus-controller";
import { MarkedFormConfirmationController } from "./controllers/marked-form-confirmation-controller";
import { PublicNavigationController } from "./controllers/public-navigation-controller";
import { ActiveTaskDrawerController } from "./controllers/active-task-drawer-controller";
import type { ActiveTaskSocket } from "./controllers/active-task-drawer-controller";
import { LandingLoginController } from "./controllers/landing-login-controller";
import { LostPasswordController } from "./controllers/lost-password-controller";
import { setLiveDataState, setMainWorkspaceState } from "./controllers/main-map-state";
import { SignupFormController } from "./controllers/signup-form-controller";
import { normalizeNumericInputValidation } from "./core/numeric-input-validation";

interface PageController {
  activeTaskSocket?(): ActiveTaskSocket | null;
  connect(): void;
  disconnect?(): void;
}

const MAP_CHUNK_FAILURE_MESSAGE = "The map interface could not be loaded.";
const BUILDING_REPORT_CHUNK_FAILURE_MESSAGE =
  "The building coverage report could not be loaded.";
const BUILD_CHUNK_FAILURE_MESSAGE =
  "The live build interface could not be loaded. Build progress remains available from the last server-rendered state.";
const DRONE_CHUNK_FAILURE_MESSAGE = "The drone flight area could not be loaded.";
const INTERFERENCE_CHUNK_FAILURE_MESSAGE = "The interference source workflow could not be loaded.";

/**
 * Boots the browser controllers needed by the current server-rendered page.
 */
export async function bootstrapApplication(documentRef: Document): Promise<void> {
  const page = documentRef.body.dataset.page;
  if (page === undefined) {
    return;
  }

  normalizeNumericInputValidation(documentRef);
  new ApplicationShellController(documentRef.body).connect();
  new CoverageSlidersDisclosure(documentRef.body).connect();
  new PublicNavigationController(documentRef.body).connect();
  const controllers: PageController[] = [];
  const billingChip = documentRef.querySelector<HTMLElement>("[data-billing-chip]");
  if (billingChip !== null) {
    const { BillingChipController } = await import("./controllers/billing-chip-controller");
    controllers.push(new BillingChipController(billingChip));
  }
  switch (page) {
    case "rf-lesson": {
      const fsplForm = documentRef.querySelector<HTMLFormElement>("[data-fspl-form]");
      if (fsplForm !== null) {
        const { FreeSpacePathLossLessonController } = await import(
          "./controllers/free-space-path-loss-lesson-controller"
        );
        controllers.push(new FreeSpacePathLossLessonController(fsplForm));
      }
      const atmosphericForm = documentRef.querySelector<HTMLFormElement>("[data-atmosphere-form]");
      if (atmosphericForm !== null) {
        const { AtmosphericAttenuationLessonController } = await import(
          "./controllers/atmospheric-attenuation-lesson-controller"
        );
        controllers.push(new AtmosphericAttenuationLessonController(atmosphericForm));
      }
      const { LinkBudgetLessonController } = await import(
        "./controllers/link-budget-lesson-controller"
      );
      const form = documentRef.querySelector<HTMLFormElement>("[data-link-budget-form]");
      if (form !== null) {
        controllers.push(new LinkBudgetLessonController(form));
      }
      break;
    }
    case "public-terrain-profile": {
      const { PublicTerrainProfileController } = await import(
        "./controllers/public-terrain-profile-controller"
      );
      controllers.push(new PublicTerrainProfileController(documentRef.body));
      break;
    }
    case "public-viewshed": {
      const { PublicViewshedController } = await import(
        "./controllers/public-viewshed-controller"
      );
      controllers.push(new PublicViewshedController(documentRef.body));
      break;
    }
    case "equipment-index": {
      const { EquipmentCatalogController } = await import(
        "./controllers/equipment-catalog-controller"
      );
      controllers.push(new EquipmentCatalogController(documentRef.body));
      break;
    }
    case "equipment-request": {
      const { EquipmentIntakeController } = await import(
        "./controllers/equipment-intake-controller"
      );
      const form = documentRef.querySelector<HTMLFormElement>("[data-equipment-intake-form]");
      if (form !== null) {
        controllers.push(new EquipmentIntakeController(form));
      }
      break;
    }
    case "equipment-vendor": {
      const { VendorIntakeController } = await import(
        "./controllers/vendor-intake-controller"
      );
      const form = documentRef.querySelector<HTMLFormElement>("[data-vendor-intake-form]");
      if (form !== null) {
        controllers.push(new VendorIntakeController(form));
      }
      break;
    }
    case "equipment-sheet":
    case "equipment-compare": {
      const { EquipmentPatternController } = await import(
        "./controllers/equipment-pattern-controller"
      );
      documentRef.querySelectorAll<HTMLElement>("[data-equipment-pattern]").forEach((root) => {
        controllers.push(new EquipmentPatternController(root));
      });
      const comparison = documentRef.querySelector<HTMLElement>("[data-coverage-comparison]");
      if (comparison !== null) {
        const { EquipmentCoverageComparisonController } = await import(
          "./controllers/equipment-coverage-comparison-controller"
        );
        const controller = new EquipmentCoverageComparisonController(comparison);
        controllers.push(controller);
      }
      break;
    }
    case "backhaul-edit": {
      const { LicensedBackhaulWorkflowController } = await import(
        "./controllers/licensed-backhaul-workflow-controller"
      );
      const workflow = documentRef.querySelector<HTMLElement>("[data-licensed-backhaul-workflow]");
      if (workflow !== null) {
        controllers.push(new LicensedBackhaulWorkflowController(workflow));
      }
      break;
    }
    case "landing": {
      controllers.push(new LandingLoginController(documentRef.body));
      const { LandingEvidenceCarouselController } = await import(
        "./controllers/landing-evidence-carousel-controller"
      );
      controllers.push(new LandingEvidenceCarouselController(documentRef.body));
      break;
    }
    case "availability": {
      const { PublicAvailabilityController } = await import(
        "./controllers/public-availability-controller"
      );
      controllers.push(new PublicAvailabilityController(documentRef.body));
      controllers.push(await loadSignupQualityMapController(documentRef));
      break;
    }
    case "lost-password-request":
    case "lost-password-reset":
      controllers.push(new LostPasswordController(documentRef.body));
      break;
    case "signup":
      controllers.push(new SignupFormController(documentRef.body));
      break;
    case "signup-test": {
      const { SignupTestController } = await import(
        "./controllers/signup-test-controller"
      );
      controllers.push(new SignupTestController(documentRef.body));
      break;
    }
    case "signup-test-terrain": {
      controllers.push(await loadSignupQualityMapController(documentRef));
      const { AdminSandboxCheckoutController } = await import(
        "./controllers/admin-sandbox-checkout-controller"
      );
      controllers.push(new AdminSandboxCheckoutController(documentRef.body));
      break;
    }
    case "signup-data-quality": {
      controllers.push(await loadSignupQualityMapController(documentRef));
      break;
    }
    case "signup-profile": {
      const { SignupProfileController } = await import(
        "./controllers/signup-profile-controller"
      );
      controllers.push(new SignupProfileController(documentRef.body));
      break;
    }
    case "tower-manager": {
      controllers.push(
        await loadPageController(
          async () => {
            const { TowerManagerController } = await import("./controllers/tower-manager-controller");
            return new TowerManagerController(documentRef.body);
          },
          () => {
            renderTowerManagerChunkFallback(documentRef);
          }
        )
      );
      break;
    }
    case "tower-list": {
      const { TowerListController } = await import("./controllers/tower-list-controller");
      controllers.push(new TowerListController(documentRef.body));
      break;
    }
    case "tower-placement": {
      controllers.push(
        await loadPageController(
          async () => {
            const { TowerPlacementController } = await import(
              "./controllers/tower-placement-controller"
            );
            return new TowerPlacementController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "client-list": {
      const { ClientListController } = await import("./controllers/client-list-controller");
      controllers.push(new ClientListController(documentRef.body));
      controllers.push(new MarkedFormConfirmationController(documentRef.body));
      break;
    }
    case "client-edit": {
      const { ClientEditController } = await import("./controllers/client-edit-controller");
      controllers.push(new ClientEditController(documentRef.body));
      break;
    }
    case "client-plan-edit": {
      const { PlanEditController } = await import("./controllers/plan-edit-controller");
      controllers.push(new PlanEditController(documentRef.body));
      break;
    }
    case "admin-billing-support": {
      // The redirect notice must receive focus and [data-confirm] forms must keep their guard.
      new BillingFocusController(documentRef.body).connect();
      new MarkedFormConfirmationController(documentRef.body).connect();
      break;
    }
    case "admin-sandbox-signup-claim": {
      new MarkedFormConfirmationController(documentRef.body).connect();
      break;
    }
    case "customer-leads": {
      if (documentRef.querySelector("[data-lead-dialog]") !== null) {
        const { LeadWorkspaceController } = await import("./controllers/lead-workspace-controller");
        controllers.push(new LeadWorkspaceController(documentRef.body));
      }
      controllers.push(
        await loadPageController(
          async () => {
            const { CustomerLeadsController } = await import(
              "./controllers/customer-leads-controller"
            );
            return new CustomerLeadsController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      if (documentRef.querySelector("[data-lead-analytics]") !== null) {
        const { LeadAnalyticsController } = await import("./controllers/lead-analytics-controller");
        controllers.push(new LeadAnalyticsController(documentRef.body));
      }
      break;
    }
    case "interference-source": {
      controllers.push(
        await loadPageController(
          async () => {
            const { InterferenceSourceController } = await import(
              "./controllers/interference-source-controller"
            );
            return new InterferenceSourceController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, INTERFERENCE_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "customer-lead-detail": {
      const { LeadAnalysisController } = await import("./controllers/lead-analysis-controller");
      const { LeadWorkspaceController } = await import("./controllers/lead-workspace-controller");
      controllers.push(new LeadAnalysisController(documentRef.body));
      controllers.push(new LeadWorkspaceController(documentRef.body));
      break;
    }
    case "tower-fine-tune": {
      controllers.push(
        await loadPageController(
          async () => {
            const { TowerFineTuneController } = await import(
              "./controllers/tower-fine-tune-controller"
            );
            return new TowerFineTuneController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "tower-service-detail": {
      controllers.push(
        await loadPageController(
          async () => {
            const { TowerServiceDetailController } = await import(
              "./controllers/tower-service-detail-controller"
            );
            return new TowerServiceDetailController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "settings":
    case "account": {
      const { AccountController } = await import(
        "./controllers/account-controller"
      );
      controllers.push(new AccountController(documentRef.body));
      const { hydrateCnHeatPreviewSelection } = await import(
        "./controllers/cnheat-preview-selection"
      );
      hydrateCnHeatPreviewSelection(documentRef.body, window.sessionStorage);
      break;
    }
    case "account-exclusions": {
      controllers.push(
        await loadPageController(
          async () => {
            const { AccountExclusionsController } = await import(
              "./controllers/account-exclusions-controller"
            );
            return new AccountExclusionsController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "account-billing": {
      // Both connect before the map chunk loads so redirect notices keep focus and
      // destructive forms keep their confirmation guard when that chunk fails.
      new BillingFocusController(documentRef.body).connect();
      new MarkedFormConfirmationController(documentRef.body).connect();
      controllers.push(
        await loadPageController(
          async () => {
            const { BillingMapController } = await import(
              "./controllers/billing-map-controller"
            );
            return new BillingMapController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "admin-antenna-edit": {
      const { AdminAntennaEditorController } = await import(
        "./controllers/admin-antenna-editor-controller"
      );
      controllers.push(new AdminAntennaEditorController(documentRef.body));
      break;
    }
    case "admin-radio-edit": {
      const { AdminRadioEditorController } = await import(
        "./controllers/admin-radio-editor-controller"
      );
      controllers.push(new AdminRadioEditorController(documentRef.body));
      break;
    }
    case "admin-user-map": {
      controllers.push(
        await loadPageController(
          async () => {
            const { AdminUserMapController } = await import(
              "./controllers/admin-user-map-controller"
            );
            return new AdminUserMapController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "admin-lidar-coverage": {
      controllers.push(
        await loadPageController(
          async () => {
            const { AdminLidarCoverageController } = await import(
              "./controllers/admin-lidar-coverage-controller"
            );
            return new AdminLidarCoverageController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "admin-sandbox-checkout": {
      const { AdminSandboxCheckoutController } = await import(
        "./controllers/admin-sandbox-checkout-controller"
      );
      controllers.push(new AdminSandboxCheckoutController(documentRef.body));
      break;
    }
    case "build": {
      controllers.push(
        await loadPageController(
          async () => {
            const { BuildStatusController } = await import("./controllers/build-status-controller");
            return new BuildStatusController(documentRef.body);
          },
          () => {
            renderBuildChunkFallback(documentRef, BUILD_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "app-gl": {
      controllers.push(
        await loadPageController(
          async () => {
            const { MainGlMapController } = await import("./controllers/main-gl-map-controller");
            return new MainGlMapController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      const { AppGlNavigationHelpController } = await import(
        "./controllers/app-gl-navigation-help-controller"
      );
      controllers.push(new AppGlNavigationHelpController(documentRef.body));
      break;
    }
    case "terrain3d": {
      controllers.push(
        await loadPageController(
          async () => {
            const { Terrain3dController } = await import("./controllers/terrain3d-controller");
            return new Terrain3dController(documentRef.body);
          },
          () => {
            renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "drone": {
      controllers.push(
        await loadPageController(
          async () => {
            const { DroneController } = await import("./controllers/drone-controller");
            return new DroneController(documentRef.body);
          },
          () => {
            renderDroneChunkFallback(documentRef, DRONE_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    case "field": {
      const { FieldViewController } = await import("./controllers/field-view-controller");
      controllers.push(new FieldViewController(documentRef.body));
      break;
    }
    case "building-report": {
      controllers.push(
        await loadPageController(
          async () => {
            const { BuildingReportController } = await import(
              "./controllers/building-report-controller"
            );
            return new BuildingReportController(documentRef.body);
          },
          () => {
            renderBuildingReportChunkFallback(documentRef, BUILDING_REPORT_CHUNK_FAILURE_MESSAGE);
          }
        )
      );
      break;
    }
    default:
      break;
  }

  const activeTaskSocket = controllers
    .map((controller) => controller.activeTaskSocket?.() ?? null)
    .find((socket) => socket !== null);
  const topbar = documentRef.body.querySelector<HTMLElement>(".lm-map-topbar, .lm-topbar");
  const mapBuildProgress = documentRef.body.querySelector<HTMLElement>("[data-coverage-progress]");
  if (activeTaskSocket !== undefined && topbar !== null && mapBuildProgress === null) {
    controllers.unshift(new ActiveTaskDrawerController(documentRef.body, activeTaskSocket));
  }

  for (const controller of controllers) {
    controller.connect();
  }
  if (controllers.some((controller) => controller.disconnect !== undefined)) {
    window.addEventListener("pagehide", () => {
      for (const controller of controllers) {
        controller.disconnect?.();
      }
    }, { once: true });
  }
}

async function loadPageController(
  load: () => Promise<PageController>,
  renderFallback: () => void
): Promise<PageController> {
  try {
    return await load();
  } catch (error) {
    renderFallback();
    throw error;
  }
}

async function loadSignupQualityMapController(documentRef: Document): Promise<PageController> {
  return loadPageController(
    async () => {
      const { SignupQualityMapController } = await import(
        "./controllers/signup-quality-map-controller"
      );
      return new SignupQualityMapController(documentRef.body);
    },
    () => {
      renderMapChunkFallback(documentRef, MAP_CHUNK_FAILURE_MESSAGE);
    }
  );
}

function renderDroneChunkFallback(documentRef: Document, message: string): void {
  const status = documentRef.body.querySelector<HTMLElement>("[data-drone-status]");
  const fallback = documentRef.body.querySelector<HTMLElement>("[data-drone-fallback]");
  if (status !== null) {
    status.textContent = message;
  }
  if (fallback !== null) {
    fallback.hidden = false;
    fallback.textContent = message;
  }
}

function renderBuildingReportChunkFallback(documentRef: Document, message: string): void {
  const status = documentRef.body.querySelector<HTMLElement>("[data-building-status]");
  const fallback = documentRef.body.querySelector<HTMLElement>("[data-building-fallback]");
  if (status !== null) {
    status.textContent = message;
  }
  if (fallback !== null) {
    fallback.hidden = false;
    fallback.textContent = message;
  }
}

/**
 * Marks map-owned browser UI unavailable when a map controller chunk fails to load.
 */
export function renderMapChunkFallback(documentRef: Document, message: string): void {
  const fallbackCard =
    documentRef.body.querySelector<HTMLElement>("[data-map-fallback]") ??
    documentRef.body.querySelector<HTMLElement>("[data-fine-tune-fallback]") ??
    documentRef.body.querySelector<HTMLElement>("[data-service-preview-fallback]") ??
    documentRef.body.querySelector<HTMLElement>("[data-customer-lead-map-warning]") ??
    documentRef.body.querySelector<HTMLElement>("[data-interference-source-fallback]");
  if (fallbackCard !== null) {
    fallbackCard.hidden = false;
    fallbackCard.textContent = message;
  }

  const fineTuneFields = documentRef.body.querySelector<HTMLElement>("[data-fine-tune-fallback-fields]");
  if (fineTuneFields !== null) {
    fineTuneFields.hidden = false;
    for (const input of fineTuneFields.querySelectorAll<HTMLInputElement>("input")) {
      input.disabled = false;
    }
  }

  const mapStatuses = [
    documentRef.body.querySelector<HTMLElement>("[data-map-status]"),
    documentRef.body.querySelector<HTMLElement>("[data-fine-tune-map-summary]"),
    documentRef.body.querySelector<HTMLElement>("[data-service-preview-map-summary]"),
    documentRef.body.querySelector<HTMLElement>("[data-interference-source-status]"),
    documentRef.body.querySelector<HTMLElement>("[data-interference-form-status]")
  ];
  for (const mapStatus of mapStatuses) {
    if (mapStatus !== null) {
      mapStatus.textContent = message;
    }
  }

  const mapRoot = documentRef.body.querySelector<HTMLElement>("[data-main-map]");
  const workspaceStateIndicator = documentRef.body.querySelector<HTMLElement>(
    "[data-workspace-state-indicator]"
  );
  setMainWorkspaceState(mapRoot, workspaceStateIndicator, "unavailable");

  const liveDataIndicator = documentRef.body.querySelector<HTMLElement>(
    "[data-live-data-indicator]"
  );
  setLiveDataState(liveDataIndicator, "unavailable");
}

function renderBuildChunkFallback(documentRef: Document, message: string): void {
  const fallbackCard = documentRef.body.querySelector<HTMLElement>("[data-build-map-fallback]");
  if (fallbackCard !== null) {
    fallbackCard.hidden = false;
    fallbackCard.textContent = message;
  }

  const connectionState = documentRef.body.querySelector<HTMLElement>("[data-build-connection-state]");
  if (connectionState !== null) {
    connectionState.hidden = false;
    connectionState.textContent = message;
  }
}

function renderTowerManagerChunkFallback(documentRef: Document): void {
  const fallbackCard = documentRef.body.querySelector<HTMLElement>("[data-tower-manager-fallback]");
  if (fallbackCard !== null) {
    fallbackCard.hidden = false;
  }

  const fallbackFields = documentRef.body.querySelector<HTMLElement>("[data-tower-manager-fallback-fields]");
  if (fallbackFields !== null) {
    fallbackFields.hidden = false;
    const textarea = fallbackFields.querySelector<HTMLTextAreaElement>("textarea");
    if (textarea !== null) {
      textarea.disabled = false;
      textarea.name = "towers_json";
    }
  }
}
